import { useEffect, useLayoutEffect, useRef, useState } from "react"

export const REVEAL_TUNING = {
  baseCharsPerSecond: 140,
  catchUpFactor: 2,
  maxLagChars: 600,
  maxCharsPerTick: 12,
  tickMs: 16,
}

export const FINISHED_ANSWER_TUNING = {
  baseCharsPerSecond: 300,
  catchUpFactor: 2,
  maxLagChars: Infinity,
  maxCharsPerTick: 12,
  tickMs: 16,
}

export function findWordBoundary(text, targetLength) {
  if (targetLength >= text.length) return text.length

  let index = targetLength
  while (index > 0 && !/\s/.test(text[index])) {
    index--
  }
  if (index === 0 && !/\s/.test(text[0])) {
    index = targetLength
  }
  while (index > 0 && text[index - 1] === "\\") {
    index--
  }
  return index
}

export default function useSmoothRevealText(text, { isStreaming = true, tuning = REVEAL_TUNING, onComplete } = {}) {
  const [revealedText, setRevealedText] = useState(() => (isStreaming ? "" : text))
  const [wasStreaming, setWasStreaming] = useState(isStreaming)
  const displayedLengthRef = useRef(isStreaming ? 0 : text.length)
  const idealLengthRef = useRef(isStreaming ? 0 : text.length)
  const targetTextRef = useRef(text)
  const lastTickRef = useRef(null)
  const onCompleteRef = useRef(onComplete)
  const hasFiredCompleteRef = useRef(false)

  if (isStreaming !== wasStreaming) {
    setWasStreaming(isStreaming)
    if (isStreaming) setRevealedText("")
  }

  useEffect(() => {
    onCompleteRef.current = onComplete
  })

  useEffect(() => {
    if (text.length < displayedLengthRef.current) {
      displayedLengthRef.current = 0
      idealLengthRef.current = 0
      setRevealedText("")
    }
    targetTextRef.current = text
  }, [text])

  useLayoutEffect(() => {
    if (!isStreaming) return undefined

    displayedLengthRef.current = 0
    idealLengthRef.current = 0
    hasFiredCompleteRef.current = false
    lastTickRef.current = Date.now()

    function fireComplete() {
      if (hasFiredCompleteRef.current) return
      hasFiredCompleteRef.current = true
      onCompleteRef.current?.()
    }

    const intervalId = setInterval(() => {
      const now = Date.now()
      const elapsedSeconds = (now - lastTickRef.current) / 1000
      lastTickRef.current = now

      const target = targetTextRef.current
      const backlog = target.length - displayedLengthRef.current
      if (backlog === 0) {
        fireComplete()
        clearInterval(intervalId)
        return
      }

      if (backlog < 0 || backlog >= tuning.maxLagChars) {
        displayedLengthRef.current = target.length
        idealLengthRef.current = target.length
        setRevealedText(target)
        fireComplete()
        return
      }

      const charsPerSecond = tuning.baseCharsPerSecond + backlog * tuning.catchUpFactor
      idealLengthRef.current = Math.min(target.length, idealLengthRef.current + charsPerSecond * elapsedSeconds)
      const rawTargetLength = Math.min(Math.floor(idealLengthRef.current), displayedLengthRef.current + tuning.maxCharsPerTick)
      let revealLength = Math.max(findWordBoundary(target, rawTargetLength), displayedLengthRef.current)
      if (revealLength === displayedLengthRef.current && rawTargetLength === displayedLengthRef.current + tuning.maxCharsPerTick) {
        revealLength = rawTargetLength
      }
      if (revealLength === displayedLengthRef.current) return

      displayedLengthRef.current = revealLength
      setRevealedText(target.slice(0, revealLength))
      if (displayedLengthRef.current === target.length) {
        fireComplete()
      }
    }, tuning.tickMs)

    return () => {
      clearInterval(intervalId)
      lastTickRef.current = null
    }
  }, [isStreaming, tuning])

  return isStreaming ? revealedText : text
}
